import { useEffect } from 'react';
import mediaUrl from '../../service/mediaUrl';
import { ButtonLink } from '../../components/Button';
import { excerpt } from '../../utils/text';
import { formatPublicationTime } from './functions';
import type { CalendarPublication, PopoverPosition } from './types';

const POPOVER_MAX_HEIGHT = 320;
const POPOVER_MAX_WIDTH = 380;
const POPOVER_VIEWPORT_MARGIN = 12;

const POPOVER_CLASS =
    'fixed z-[1110] w-[min(100vw-24px,380px)] rounded-md border border-light-gray bg-white p-3 shadow-md';
const THUMBNAIL_CLASS = 'h-20 w-20 rounded-md object-cover';
const TITLE_CLASS = 'text-small font-medium text-dark-gray';
const CONTENT_CLASS = 'text-more-small text-gray line-clamp-4';

function clampPosition(position: PopoverPosition): PopoverPosition {
    const width = Math.min(window.innerWidth - 24, POPOVER_MAX_WIDTH);
    const top = Math.min(position.top, window.innerHeight - POPOVER_MAX_HEIGHT);
    const left = Math.max(
        POPOVER_VIEWPORT_MARGIN,
        Math.min(position.left, window.innerWidth - width - POPOVER_VIEWPORT_MARGIN),
    );
    return { top, left };
}

type PublicationDetailPopoverProps = Readonly<{
    publication: CalendarPublication;
    position: PopoverPosition;
    onClose: () => void;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
}>;

export function PublicationDetailPopover({
    publication,
    position,
    onClose,
    onMouseEnter,
    onMouseLeave,
}: PublicationDetailPopoverProps) {
    const { top, left } = clampPosition(position);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    return (
        <div
            className={POPOVER_CLASS}
            style={{ top, left }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="calendar-popover-title"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <div className="flex gap-2">
                <img
                    src={mediaUrl(publication)}
                    alt={publication.title}
                    className={THUMBNAIL_CLASS}
                />
                <div className="min-w-0">
                    <p id="calendar-popover-title" className={`mb-1 ${TITLE_CLASS}`}>
                        {formatPublicationTime(publication.publicationDate)} - {publication.title}
                    </p>
                    <p className={CONTENT_CLASS}>{excerpt(publication.content, 70)}</p>
                </div>
            </div>
            <div className="mt-3 flex">
                <ButtonLink to={`/show_publication/${publication.id}`} onClick={onClose}>
                    Voir la publication
                </ButtonLink>
            </div>
        </div>
    );
}
